import { useEffect, useState } from "react";
import { Lock, Unlock, RefreshCw, Mic, Loader2, Trash2, ImageIcon, Upload, Download, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg, fileUrl, uploadFile, safeName } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Portrait = ({ path, label, generating, t, onUpload, locked, dlName }) => (
  <div className={`group aspect-[3/4] rounded-md bg-[#11141E] border border-[#2A3245] overflow-hidden relative ${generating ? "scan" : ""}`} data-testid={t}>
    {path ? <img src={fileUrl(path)} alt={label} className="w-full h-full object-cover" />
      : <div className="w-full h-full grid place-items-center text-slate-600"><ImageIcon className="w-6 h-6" /></div>}
    <span className="absolute bottom-1 left-1 font-mono text-[9px] bg-black/60 px-1.5 py-0.5 rounded">{label}</span>
    <div className="absolute top-1 right-1 flex gap-1">
      {!locked && (
        <label data-testid={`${t}-upload`} title="Upload photo" className="cursor-pointer p-1.5 rounded bg-black/60 hover:bg-black/80 transition-colors">
          <Upload className="w-3 h-3" />
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; if (f) onUpload(f); }} />
        </label>
      )}
      {path && (
        <a data-testid={`${t}-download`} title="Download" href={fileUrl(path, dlName)} className="p-1.5 rounded bg-black/60 hover:bg-black/80 transition-colors"><Download className="w-3 h-3" /></a>
      )}
    </div>
  </div>
);

export const CharacterCard = ({ c, voices, reload }) => {
  const [f, setF] = useState({ name: c.name, description: c.description, voice: c.voice, voice_text: c.voice_text });
  const [busyVoice, setBusyVoice] = useState(false);
  const [busyLock, setBusyLock] = useState(false);
  useEffect(() => { setF({ name: c.name, description: c.description, voice: c.voice, voice_text: c.voice_text }); }, [c.name, c.description, c.voice, c.voice_text]);
  const gen = c.portrait_status === "generating";
  const call = async (fn, ok) => { try { await fn(); if (ok) toast.success(ok); await reload(); } catch (e) { toast.error(errMsg(e)); } };
  const saveLook = () => http.patch(`/characters/${c.id}`, { name: f.name, description: f.description });
  const genPortraits = () => call(async () => { await saveLook(); await http.post(`/characters/${c.id}/portraits`); });
  const genSide = () => call(() => http.post(`/characters/${c.id}/portraits`, null, { params: { side_only: true } }));
  const upPhoto = (view) => (file) => call(() => uploadFile(`/characters/${c.id}/upload-portrait?view=${view}`, file),
    view === "front" ? "Front photo uploaded — add or make a side view next" : "Side photo uploaded");
  const upVoice = async (file) => {
    setBusyVoice(true);
    await call(() => uploadFile(`/characters/${c.id}/upload-voice`, file), "Voice uploaded");
    setBusyVoice(false);
  };
  const n = safeName(c.name);
  const genVoice = async () => {
    setBusyVoice(true);
    await call(async () => { await http.patch(`/characters/${c.id}`, { voice: f.voice, voice_text: f.voice_text }); await http.post(`/characters/${c.id}/voice`); });
    setBusyVoice(false);
  };
  const lock = async (k, v) => {
    setBusyLock(true);
    await call(() => http.post(`/characters/${c.id}/lock`, { [k]: v }), v ? "Locked — used in every clip" : "Unlocked");
    setBusyLock(false);
  };
  const remove = () => window.confirm(`Remove ${c.name}?`) && call(() => http.delete(`/characters/${c.id}`));
  const t = c.id;

  return (
    <div className={`card-s p-4 space-y-4 rise ${c.looks_locked && c.voice_locked ? "border-amber-500/50" : ""}`} data-testid={`character-card-${t}`}>
      <div className="flex items-center gap-2">
        <Input data-testid={`char-name-${t}`} disabled={c.looks_locked} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="font-display text-base" />
        <button data-testid={`char-delete-${t}`} onClick={remove} className="text-slate-500 hover:text-red-400 p-2"><Trash2 className="w-4 h-4" /></button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Portrait t={`char-front-${t}`} path={c.portrait_front} label="FRONT" generating={gen} locked={c.looks_locked} onUpload={upPhoto("front")} dlName={`${n}_front.jpg`} />
        <Portrait t={`char-side-${t}`} path={c.portrait_side} label="SIDE" generating={gen} locked={c.looks_locked} onUpload={upPhoto("side")} dlName={`${n}_side.jpg`} />
      </div>
      {c.portrait_front && !c.portrait_side && !c.looks_locked && (
        <Button data-testid={`char-gen-side-${t}`} size="sm" variant="outline" disabled={gen} onClick={genSide} className="w-full rounded-full">
          <Wand2 className="w-3.5 h-3.5 mr-1.5" />Make side view from front photo
        </Button>
      )}
      {c.portrait_status === "failed" && <div className="text-xs text-red-400" data-testid={`char-portrait-error-${t}`}>{c.portrait_error}</div>}
      <Textarea data-testid={`char-desc-${t}`} disabled={c.looks_locked} rows={3} placeholder="Age, look, hair, clothing, personality"
        value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} />
      <div className="flex gap-2">
        <Button data-testid={`char-gen-portraits-${t}`} size="sm" variant="secondary" disabled={c.looks_locked || gen} onClick={genPortraits} className="flex-1 rounded-full">
          {gen ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1.5" />}
          {gen ? "Generating…" : c.portrait_front ? "Regenerate look" : "Generate look"}
        </Button>
        <Button data-testid={`char-lock-look-${t}`} size="sm" variant={c.looks_locked ? "default" : "outline"} onClick={() => lock("looks_locked", !c.looks_locked)} className="rounded-full">
          {c.looks_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
        </Button>
      </div>
      <div className="border-t border-[#2A3245] pt-4 space-y-2">
        <div className="font-mono text-[10px] tracking-widest text-cyan-400">VOICE</div>
        <p className="text-[11px] text-slate-500">Sample needs 6+ seconds of speech. In-video dialogue is spoken in English.</p>
        <Select disabled={c.voice_locked} value={f.voice} onValueChange={(v) => setF({ ...f, voice: v })}>
          <SelectTrigger data-testid={`char-voice-select-${t}`}><SelectValue /></SelectTrigger>
          <SelectContent>
            {c.voice === "uploaded" && c.voice_sample && <SelectItem value="uploaded">Uploaded voice</SelectItem>}
            {voices.map((v) => <SelectItem key={v.id} value={v.id}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid={`char-voice-text-${t}`} disabled={c.voice_locked} value={f.voice_text} onChange={(e) => setF({ ...f, voice_text: e.target.value })} placeholder="Short sample line" />
        {c.voice_sample && (
          <div className="flex items-center gap-2">
            <audio data-testid={`char-voice-audio-${t}`} controls src={fileUrl(c.voice_sample)} className="flex-1 min-w-0 h-9" />
            <a data-testid={`char-voice-download-${t}`} title="Download voice" href={fileUrl(c.voice_sample, `${n}_voice.mp3`)} className="p-2 rounded-full bg-[#212738] hover:bg-[#2A3245] transition-colors"><Download className="w-3.5 h-3.5" /></a>
          </div>
        )}
        {c.voice_locked && (c.voice_bind_ok === false || c.is_human === false) && (
          <div data-testid={`char-voice-bind-note-${t}`} className="text-xs text-amber-300/90 bg-amber-500/10 border border-amber-500/20 rounded-lg p-2">
            The video model can only attach a locked voice to a human face, so after each clip the app dubs this character's locked voice onto their lines.
          </div>
        )}
        {!c.voice_locked && (
          <label data-testid={`char-voice-upload-${t}`} className={`flex items-center justify-center gap-1.5 h-8 rounded-full border border-dashed border-[#3B4662] text-xs text-slate-300 hover:border-cyan-400 transition-colors ${busyVoice ? "opacity-50 pointer-events-none" : "cursor-pointer"}`}>
            <Upload className="w-3.5 h-3.5" />Upload a voice recording (6–30 s)
            <input type="file" accept="audio/*,.mp3,.wav,.m4a" className="hidden" onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ""; if (fl) upVoice(fl); }} />
          </label>
        )}
        <div className="flex gap-2">
          <Button data-testid={`char-gen-voice-${t}`} size="sm" variant="secondary" disabled={c.voice_locked || busyVoice} onClick={genVoice} className="flex-1 rounded-full">
            {busyVoice ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Mic className="w-3.5 h-3.5 mr-1.5" />}
            {c.voice_sample ? "Regenerate voice" : "Generate voice"}
          </Button>
          <Button data-testid={`char-lock-voice-${t}`} size="sm" variant={c.voice_locked ? "default" : "outline"} disabled={busyLock} onClick={() => lock("voice_locked", !c.voice_locked)} className="rounded-full">
            {busyLock ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : c.voice_locked ? <Lock className="w-3.5 h-3.5" /> : <Unlock className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>
    </div>
  );
};
